// @ts-nocheck
import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export interface NavigationItem {
  label: string;
  link: string;
  active?: boolean;
  pageType?: string; // Optional pageType for click handler
}

export interface Logo {
  image?: string;
  alt?: string;
  text?: string;
  link?: string;
}

export interface CTAButton {
  text: string;
  link?: string;
  backgroundColor?: string;
  textColor?: string;
}

/**
 * Component for rendering a customizable page header with logo, navigation, CTA and responsive mobile menu.
 * Handles click behavior (preview vs clickable/editor modes), maps navigation and CTA links to internal page types,
 * manages mobile menu state and provides immediate visual feedback for active navigation items.
 * Key methods:
 *   - onHeaderClick(event): emits headerClick when clickable and header area (excluding nav/menu controls) is clicked.
 *   - onNavigationItemClick(item, event): prevents duplicate clicks, derives a pageType from the item (or its link), updates local active state for instant UI feedback, emits navigationClick, and closes mobile menu.
 *   - onCTAButtonClick(event): maps CTA link to a pageType (using link patterns and navigation matching) and emits navigationClick.
 *   - toggleMobileMenu(event), closeMobileMenu(): control mobile menu open/close state.
 *   - isNavigationItemActive(item, index): determines active state using local override or item's active property.
 *   - onDocumentClick(event) [HostListener]: closes mobile menu when clicking outside.
 *   - onEscapeKey() [HostListener]: closes mobile menu on Escape key.
 * Lifecycle hooks:
 *   - ngOnChanges(changes): synchronizes local activeNavigationIndex when navigation input changes and sets sensible defaults.
 * @component
 * @selector app-champs-page-header
 * @example
 *   <app-champs-page-header [navigation]="navItems" [logo]="siteLogo" [ctaButton]="primaryCta" [clickable]="true" (headerClick)="onHeader()" (navigationClick)="onNavigate($event)"></app-champs-page-header>
 * @input {Logo | null} logo - Logo object to display on the header (nullable).
 * @input {NavigationItem[]} navigation - Array of navigation items rendered in the header; items may include link, pageType and active properties.
 * @input {CTAButton | null} ctaButton - Optional call-to-action button configuration (nullable).
 * @input {boolean} visible - Whether the header is visible; can be used to hide/show header.
 * @input {boolean} clickable - If true, header emits headerClick on header area clicks (editor mode); when false it behaves as preview mode.
 * @input {boolean} selected - Whether this header is marked selected (used for editor UI state).
 * @input {string | null} headingColor - Optional branding color for heading text; fallback is used when not provided.
 * @input {string | null} headingFont - Optional branding font for heading text; fallback is used when not provided.
 * @input {string | null} backgroundColor - Optional background color for the header area.
 * @input {'desktop'|'tablet'|'mobile'} deviceType - Device type used to apply responsive classes and behavior (defaults to 'desktop').
 * @output {EventEmitter<void>} headerClick - Emitted when the clickable header area is clicked (excluding nav/CTA/menu controls).
 * @output {EventEmitter<string>} navigationClick - Emitted with a resolved pageType string when a navigation item or CTA is activated.
 */
@Component({
  selector: 'app-champs-page-header',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[class.clickable]': 'clickable',
    '[class.preview-mode]': '!clickable',
    '[class.device-desktop]': "deviceType === 'desktop'",
    '[class.device-tablet]': "deviceType === 'tablet'",
    '[class.device-mobile]': "deviceType === 'mobile'",
    '[class.in-hero]': 'inHero',
  },
})
export class ChampsPageHeaderComponent implements OnChanges {
  @Input() logo: Logo | null = null;
  @Input() navigation: NavigationItem[] = [];
  @Input() ctaButton: CTAButton | null = null;
  @Input() visible = true;
  @Input() clickable = false;
  @Input() selected = false;
  @Input() headingColor: string | null = null; // Heading color from branding
  @Input() headingFont: string | null = null; // Heading font from branding
  @Input() backgroundColor: string | null = null; // Background color for header
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop'; // Device type for responsive behavior
  @Input() inHero = false; // True when header is placed inside the hero section
  @Output() headerClick = new EventEmitter<void>();
  @Output() navigationClick = new EventEmitter<string>();

  // Mobile menu state
  isMobileMenuOpen = false;

  // Prevent duplicate clicks
  private isNavigating = false;

  // Track active navigation item locally for immediate visual feedback
  private activeNavigationIndex: number | null = null;

  /**
   * Handles header click events: if the component is clickable, emits headerClick unless the click originated on navigation or menu elements.
   * @example
   * nHeaderClick(new Event('click'))
   * undefined
   * @param {{Event}} {{event}} - The click event to process; clicks on .nav-link, .book-wash-btn, .hamburger-menu, .mobile-menu-overlay, or .mobile-menu-drawer are ignored.
   * @returns {{void}} Does not return a value.
   **/
  onHeaderClick(event: Event): void {
    if (this.clickable) {
      // Don't propagate if clicking on navigation items or menu button
      const target = event.target as HTMLElement;
      if (
        target.closest('.nav-link') ||
        target.closest('.book-wash-btn') ||
        target.closest('.hamburger-menu') ||
        target.closest('.mobile-menu-overlay') ||
        target.closest('.mobile-menu-drawer')
      ) {
        return;
      }
      this.headerClick.emit();
    }
  }

  /**
   * Handle a navigation item click: prevent duplicate clicks, update active state, resolve a pageType from the item (using item.pageType or heuristics on item.link), emit the resolved pageType, close mobile menu, and reset navigation flag after a short delay.
   * @example
   * nNavigationItemClick({ pageType: 'home', link: '/' }, new Event('click'))
   * void
   * @param {{NavigationItem}} {{item}} - The navigation item that was clicked; may include pageType and/or link used to determine the destination.
   * @param {{Event}} {{event}} - The click Event; used to prevent default behavior and stop propagation.
   * @returns {{void}} Returns nothing.
   **/
  onNavigationItemClick(item: NavigationItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    // Prevent duplicate clicks
    if (this.isNavigating) {
      return;
    }
    this.isNavigating = true;

    // Update active state immediately for visual feedback
    const clickedIndex = this.navigation.findIndex(nav => nav === item);
    if (clickedIndex !== -1) {
      this.activeNavigationIndex = clickedIndex;
      // Also update the item's active property for immediate visual update
      this.navigation.forEach((nav, index) => {
        nav.active = index === clickedIndex;
      });
    }

    const { pageType: itemPageType, link: itemLink } = item;
    let pageType = itemPageType;
    if (!pageType && itemLink) {
      const normalizedLink = itemLink
        .replace(/^#+|\/+$/g, '')
        .replace(/^\/+/, '')
        .toLowerCase();

      // Map common link patterns to pageType
      if (normalizedLink === '' || normalizedLink === 'home' || normalizedLink === '/') {
        pageType = 'home';
      } else if (
        normalizedLink === 'expresswashes' ||
        normalizedLink === 'express-washes' ||
        normalizedLink.includes('express-wash')
      ) {
        pageType = 'express-wash-packages';
      } else if (
        normalizedLink === 'expressdetails' ||
        normalizedLink === 'express-details' ||
        normalizedLink.includes('express-detail') ||
        normalizedLink.includes('express-auto')
      ) {
        pageType = 'express-auto-details';
      } else if (
        normalizedLink === 'fleets' ||
        normalizedLink === 'fleet' ||
        normalizedLink.includes('fleet')
      ) {
        pageType = 'fleet-management';
      } else if (
        normalizedLink === 'coupons' ||
        normalizedLink === 'coupon' ||
        normalizedLink.includes('coupon')
      ) {
        pageType = 'coupons';
      } else if (normalizedLink === 'memberships' || normalizedLink.includes('membership')) {
        pageType = 'memberships';
      } else {
        // Try to extract pageType from query parameters: /preview?websiteId=...&pageType=...
        const [, queryString] = itemLink.split('?');
        const urlParams = new URLSearchParams(queryString || '');
        pageType = urlParams.get('pageType') || undefined;

        // If still not found, try to extract from path: /website-preview/{templateId}/{pageType}
        if (!pageType) {
          const match = itemLink.match(/\/website-preview\/[^/]+(?:\/(.+))?$/);
          if (match) {
            pageType = match[1] || 'home';
          } else {
            // Fallback: use the normalized link as pageType
            pageType = normalizedLink || 'home';
          }
        }
      }
    }

    // Default to home if still no pageType found
    if (!pageType) {
      pageType = 'home';
    }

    this.navigationClick.emit(pageType);
    // Close mobile menu when navigation item is clicked
    this.isMobileMenuOpen = false;

    // Reset navigation flag after a short delay
    setTimeout(() => {
      this.isNavigating = false;
    }, 300);
  }

  // Check if a navigation item is active
  isNavigationItemActive(item: NavigationItem, index: number): boolean {
    // Use local active state if set, otherwise fall back to item's active property
    if (this.activeNavigationIndex !== null) {
      return index === this.activeNavigationIndex;
    }
    return item.active === true;
  }

  /**
   * Handle CTA button clicks by preventing default behavior, mapping the CTA link to a page type, emitting a navigation event, and closing the mobile menu.
   * @example
   * nCTAButtonClick(event)
   * undefined
   * @param {{Event}} {{event}} - The click event from the CTA button; used to prevent default and stop propagation.
   * @returns {{void}} Emits a navigationClick with the resolved pageType and returns nothing.
   **/
  onCTAButtonClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.ctaButton?.link) return;

    // Convert link to page type - handle both hash links (#expresswashes) and regular links
    const link = this.ctaButton.link;
    // Remove hash, leading/trailing slashes and normalize
    const normalizedLink = link
      .replace(/^#+|\/+$/g, '')
      .replace(/^\/+/, '')
      .toLowerCase();

    let pageType = 'home';
    if (normalizedLink && normalizedLink !== 'home' && normalizedLink !== '') {
      // Handle hash links like #expresswashes, #expressdetails, #fleets, #coupons
      if (
        normalizedLink === 'expresswashes' ||
        normalizedLink.includes('express-wash') ||
        normalizedLink === 'express-washes'
      ) {
        pageType = 'express-wash-packages';
      } else if (
        normalizedLink === 'expressdetails' ||
        normalizedLink.includes('express-detail') ||
        normalizedLink.includes('express-auto') ||
        normalizedLink === 'express-details'
      ) {
        pageType = 'express-auto-details';
      } else if (normalizedLink === 'fleets' || normalizedLink.includes('fleet')) {
        pageType = 'fleet-management';
      } else if (normalizedLink === 'coupons' || normalizedLink.includes('coupon')) {
        pageType = 'coupons';
      } else if (normalizedLink === 'memberships' || normalizedLink.includes('membership')) {
        pageType = 'memberships';
      } else {
        // Try to match with navigation items
        const matchingNav = this.navigation.find(nav => {
          const { link: navLinkRaw } = nav;
          const navLink = (navLinkRaw || '')
            .replace(/^#+|\/+$/g, '')
            .replace(/^\/+/, '')
            .toLowerCase();
          return navLink === normalizedLink || navLink.replace(/-/g, '') === normalizedLink;
        });
        if (matchingNav && matchingNav.pageType) {
          pageType = matchingNav.pageType;
        }
      }
    }

    this.navigationClick.emit(pageType);
    // Close mobile menu when CTA button is clicked
    this.isMobileMenuOpen = false;
  }

  toggleMobileMenu(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    // Always allow menu to toggle - it should work in both editor and preview mode
    // The mobile menu overlay and drawer are only shown when clickable is false (preview mode)
    // but the hamburger button should always be functional
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  // Close mobile menu when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Close menu if clicking outside the menu and hamburger button
    if (
      this.isMobileMenuOpen &&
      !target.closest('.mobile-menu-drawer') &&
      !target.closest('.hamburger-menu')
    ) {
      this.closeMobileMenu();
    }
  }

  // Close mobile menu on escape key
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  /**
   * Update local active navigation index when the "navigation" input changes.
   * @example
   * gOnChanges({ navigation: [{ active: true }, { active: false }] })
   * undefined
   * @param {SimpleChanges} changes - Angular SimpleChanges object containing input changes (expects a 'navigation' change).
   * @returns {void} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    // Update active state when navigation input changes
    if (changes['navigation'] && this.navigation && this.navigation.length > 0) {
      // Find the active item from the input
      const activeIndex = this.navigation.findIndex(nav => nav.active === true);
      if (activeIndex !== -1) {
        this.activeNavigationIndex = activeIndex;
      } else if (this.activeNavigationIndex === null) {
        // If no active item is set and we haven't set one locally, default to first item
        this.activeNavigationIndex = 0;
      }
    }
  }

  /**
   * Get heading color from branding or use default
   */
  getHeadingColor(): string {
    return this.headingColor || '#15172E';
  }

  /**
   * Get heading font from branding or use default
   */
  getHeadingFont(): string {
    return this.headingFont || 'Inter';
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
